// Bileli · modules/media/engine — the background upload ENGINE (frontend/web's PendingUploadResumer +
// PendingFinalizeDriver + PendingLinkResumer, recoded as one framework-free singleton). why: uploads must
// survive navigation, mode switches (even chat) and reloads — so the drivers live OUTSIDE the component
// tree, started once from an always-mounted seam. Views only read the mini-stores.
//
// Three drivers on one 5s tick (+ store-change kicks). inFlight Sets are DEDUPE, not caps — N uploads run
// truly in parallel:
//   resume:   stale-heartbeat pending uploads → restore File from IDB → rebuild tus (same fingerprint) →
//             continue at the server byte offset. Expired ticket / missing blob → terminal cleanup + note.
//   finalize: bytesComplete && !resuming && !claimed → poll finalize (Bunny 409 + Retry-After +
//             encodeProgress → finalizeProgressStore) → emit mediaSettled so views refetch.
//   link:     retry idempotent binds (cover / preview / lesson contentJson) that were lost mid-flight.

import { getActions } from '../../../../global';

import type { PendingLinkEntry } from '../lib/pendingLinkStore';
import type { PendingUploadEntry } from '../lib/pendingUploadStore';
import type { BunnyTusTicket, UploadTicket } from '../model/media.types';

import { deleteUploadFile, getUploadFile } from '../lib/idbBlobStore';
import {
  clearPendingFinalizeByAssetId, listPendingFinalize, subscribePendingFinalize,
} from '../lib/pendingFinalizeStore';
import { clearPendingLinkByAssetId, listPendingLinks, subscribePendingLinks } from '../lib/pendingLinkStore';
import {
  clearPendingUploadByAssetId, listPendingUploads, subscribePendingUploads,
} from '../lib/pendingUploadStore';
import { clearUploadProgress, getUploadProgress, setUploadProgress } from '../lib/uploadProgressStore';
import { bileliApiJson } from '../../../shared';
import { finalizeUpload, MediaApiError, uploadBytes } from '../model/media.api';
import {
  finishEncode, finishResume, isResuming, setEncodePct, setEncodeQueued, setResumePct, startResume,
} from '../store/liveStores';
import { emitMediaSettled } from './mediaEvents';

const TICK_MS = 5000;
const STALE_MS = 10_000; // no heartbeat for this long → the uploader is gone → safe to resume

const JSON_HEADERS = { 'Content-Type': 'application/json' };

const resumeInFlight = new Set<string>();
const finalizeInFlight = new Set<string>();
const finalizeTerminal = new Set<string>();
const linkInFlight = new Set<string>();

let started = false;
let timer: ReturnType<typeof setInterval> | undefined;

function notify(message: string): void {
  try {
    getActions().showNotification({ message });
  } catch {
    // actions not ready — silent
  }
}

function terminalUploadCleanup(assetId: string): void {
  clearPendingUploadByAssetId(assetId);
  void deleteUploadFile(assetId);
  clearUploadProgress(assetId);
}

function rebuildTicket(entry: PendingUploadEntry): UploadTicket {
  const t: BunnyTusTicket = {
    protocol: 'BUNNY_TUS',
    asset: { id: entry.assetId },
    uploadExpiresInSec: 0,
    creationUrl: entry.ticket.creationUrl,
    tusVersion: entry.ticket.tusVersion,
    libraryId: entry.ticket.libraryId,
    videoGuid: entry.ticket.videoGuid,
    authorizationSignature: entry.ticket.authorizationSignature,
    authorizationExpire: entry.ticket.authorizationExpire,
    uploadMetadataBase64: entry.ticket.uploadMetadataBase64,
  };
  return t;
}

async function runResume(entry: PendingUploadEntry): Promise<void> {
  resumeInFlight.add(entry.assetId);
  startResume(entry.assetId);
  // Seed the badge with the FROZEN pct from the last heartbeat — tus resumes from the server offset,
  // so the badge starts at ~N% immediately instead of flashing 0% until the first progress event.
  const frozen = getUploadProgress(entry.assetId);
  if (frozen && frozen.pct > 0 && !frozen.bytesComplete) setResumePct(entry.assetId, frozen.pct);
  try {
    const file = await getUploadFile(entry.assetId);
    if (!file) { // storage cleared / another browser — cannot resume
      terminalUploadCleanup(entry.assetId);
      notify(`Загрузка «${entry.filename}» прервана — файл недоступен. Загрузите заново.`);
      emitMediaSettled({ assetId: entry.assetId, ok: false });
      return;
    }
    await uploadBytes(rebuildTicket(entry), file, (pct) => {
      setResumePct(entry.assetId, pct);
      setUploadProgress(entry.assetId, pct, false);
    });
    setUploadProgress(entry.assetId, 100, true); // bytes done → finalize driver may proceed
    clearPendingUploadByAssetId(entry.assetId);
    void deleteUploadFile(entry.assetId);
    kick(); // start finalize without waiting a tick
  } catch (error) {
    if (error instanceof MediaApiError && error.status >= 400 && error.status < 500) {
      terminalUploadCleanup(entry.assetId);
      notify(`Не удалось загрузить «${entry.filename}».`);
      emitMediaSettled({ assetId: entry.assetId, ok: false });
    }
    // transient (network / 5xx) — leave the record; next tick retries
  } finally {
    resumeInFlight.delete(entry.assetId);
    finishResume(entry.assetId);
  }
}

function scanResume(): void {
  const now = Date.now();
  for (const entry of listPendingUploads(now)) {
    if (resumeInFlight.has(entry.assetId)) continue;
    const prog = getUploadProgress(entry.assetId, now);
    if (prog?.bytesComplete) { // raced by an in-session uploader — just clean the resume state
      clearPendingUploadByAssetId(entry.assetId);
      void deleteUploadFile(entry.assetId);
      continue;
    }
    if (prog && now - prog.updatedAt < STALE_MS) continue; // a live uploader owns it
    if (entry.ticket.authorizationExpire * 1000 <= now) { // Bunny auth expired (1h TTL)
      terminalUploadCleanup(entry.assetId);
      notify(`Срок загрузки «${entry.filename}» истёк. Загрузите заново.`);
      emitMediaSettled({ assetId: entry.assetId, ok: false });
      continue;
    }
    void runResume(entry);
  }
}

async function runFinalize(assetId: string, courseId: string, lessonId?: string): Promise<void> {
  finalizeInFlight.add(assetId);
  setEncodePct(assetId, 0);
  try {
    // Bileli 2026-07-29: `queued` decides what the UI says. While Bunny's shared queue holds the video,
    // encodeProgress is null BY DESIGN (Bunny pins it to 0 for the whole wait, and rendering that as
    // "0%" reports a stall that is not happening) — so record the queued flag and do NOT push a pct.
    await finalizeUpload(assetId, ({ encodeProgress, queued }) => {
      if (queued) {
        setEncodeQueued(assetId, true);
        return;
      }
      setEncodeQueued(assetId, false);
      if (encodeProgress !== undefined) setEncodePct(assetId, encodeProgress);
    });
    clearUploadProgress(assetId);
    clearPendingFinalizeByAssetId(assetId);
    finishEncode(assetId); // settled → the badge disappears
    emitMediaSettled({ assetId, courseId, lessonId, ok: true });
  } catch (error) {
    if (error instanceof MediaApiError && error.status === 400) { // FAILED / bytes never arrived — terminal
      finalizeTerminal.add(assetId);
      clearUploadProgress(assetId);
      clearPendingFinalizeByAssetId(assetId);
      finishEncode(assetId); // terminal → nothing left to report
      notify('Обработка видео не удалась.');
      emitMediaSettled({ assetId, courseId, lessonId, ok: false });
    }
    // transient / deadline reached — re-armed on a later tick, and the LAST KNOWN state stays on screen
  } finally {
    finalizeInFlight.delete(assetId);
    // Bileli 2026-07-29 — the bug that made a slow upload look like a dead one. This used to be:
    //     finishEncode(assetId);
    // in `finally`, i.e. on EVERY exit path including the transient one, which deleted the key outright
    // and defeated the monotonic guard in setEncodePct. On a 12-minute encode the percentage vanished
    // and reset roughly every 295s (the old attempt ceiling) and never rose — the teacher saw a number
    // that reappeared as 0% forever. The driver this was ported from
    // (frontend/web pending-finalize-driver.tsx:79-97) cleared it ONLY on success or a terminal 400 and
    // deliberately left the last value on screen when a poll ran out; the port moved it into `finally`.
    // It now lives in exactly those two branches above. Do not move it back here.
  }
}

function scanFinalize(): void {
  const now = Date.now();
  for (const entry of listPendingFinalize(now)) {
    const id = entry.assetId;
    if (finalizeInFlight.has(id) || finalizeTerminal.has(id)) continue;
    // Bileli 2026-07-29: commented out WITH its registry (store/liveStores.ts) — the second producer it
    // arbitrated against was never ported, so this could only ever be false. A guard that cannot fire
    // reads as protection that is not there; uncomment both together if a per-pane poller is added.
    // if (isFinalizeClaimed(id)) continue; // a mounted pane owns this poll
    if (isResuming(id)) continue; // bytes still flowing
    const prog = getUploadProgress(id, now);
    if (prog && !prog.bytesComplete) continue; // bytes not done (a live/resumable upload exists)
    if (!prog && listPendingUploads(now).some((u) => u.assetId === id)) continue; // not started yet
    void runFinalize(id, entry.courseId, entry.lessonId);
  }
}

async function runLink(entry: PendingLinkEntry): Promise<void> {
  linkInFlight.add(entry.id);
  try {
    const { target } = entry;
    if (target.type === 'course-cover') {
      await bileliApiJson(`/courses/${target.courseId}/cover`, {
        method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ mediaId: entry.assetId }),
      });
    } else if (target.type === 'course-preview') {
      await bileliApiJson(`/courses/${target.courseId}/preview-video`, {
        method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ mediaId: entry.assetId }),
      });
    } else {
      await bileliApiJson(`/lessons/${target.lessonId}`, {
        method: 'PATCH',
        headers: JSON_HEADERS,
        body: JSON.stringify({ contentJson: { [target.field]: entry.assetId } }),
      });
    }
    clearPendingLinkByAssetId(entry.assetId);
  } catch {
    // transient — retried next tick (binds are idempotent). 4xx keeps retrying until TTL prunes it;
    // acceptable: the bind was already attempted once in-session with user-visible error handling.
  } finally {
    linkInFlight.delete(entry.id);
  }
}

function scanLink(): void {
  for (const entry of listPendingLinks()) {
    if (linkInFlight.has(entry.id)) continue;
    void runLink(entry);
  }
}

function tick(): void {
  scanResume();
  scanFinalize();
  scanLink();
}

/** Scan immediately (call after initiating an upload / registering a finalize). */
export function kick(): void {
  if (started) tick();
}

/** Idempotent; call once from an always-mounted seam. Uploads then survive any in-app navigation. */
export function start(): void {
  if (started) return;
  started = true;
  timer = setInterval(tick, TICK_MS);
  subscribePendingUploads(() => scanResume());
  subscribePendingFinalize(() => scanFinalize());
  subscribePendingLinks(() => scanLink());
  tick();
}

export function stop(): void {
  started = false;
  if (timer) clearInterval(timer);
}
